import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';

const PAGE_SIZE = 12;

/**
 * Fetch approved reviews for public display — supports pagination.
 */
export function useApprovedReviews(page = 1) {
  return useQuery({
    queryKey: ['reviews', 'approved', page],
    queryFn: async () => {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      const { data, error, count } = await supabase
        .from('testimonials')
        .select('*, products(title, slug, image_path)', { count: 'exact' })
        .eq('status', 'approved')
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      return {
        reviews: data,
        totalCount: count,
        totalPages: Math.ceil((count || 0) / PAGE_SIZE),
        currentPage: page,
      };
    },
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Fetch approved reviews for a specific product.
 */
export function useProductReviews(productId) {
  return useQuery({
    queryKey: ['reviews', 'product', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('testimonials')
        .select('*')
        .eq('product_id', productId)
        .eq('status', 'approved')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!productId,
    staleTime: 1000 * 60 * 2,
  });
}

/**
 * Submit a new review (public, no auth required).
 * Checks IP limit (max 2 reviews per IP) via the edge function.
 */
export function useSubmitReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, text, rating, product_id, email }) => {
      // Call edge function which handles IP tracking
      const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/submit-review`;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ name, text, rating, product_id, email }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit review');
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
    },
  });
}

/* ══════════════════════════════════════════════
 * ADMIN HOOKS
 * ══════════════════════════════════════════════ */

/**
 * All reviews for admin — includes pending, approved, rejected.
 */
export function useAdminReviews(statusFilter = 'all') {
  return useQuery({
    queryKey: ['admin', 'reviews', statusFilter],
    queryFn: async () => {
      let query = supabase
        .from('testimonials')
        .select('*, products(title, slug, image_path)')
        .order('created_at', { ascending: false });

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    staleTime: 1000 * 30,
  });
}

/**
 * Admin review stats — counts by status.
 */
export function useAdminReviewStats() {
  return useQuery({
    queryKey: ['admin', 'review-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('testimonials')
        .select('status');
      if (error) throw error;

      const stats = { total: data.length, pending: 0, approved: 0, rejected: 0 };
      data.forEach((r) => {
        if (stats[r.status] !== undefined) stats[r.status]++;
      });
      return stats;
    },
    staleTime: 1000 * 30,
  });
}

/**
 * Update review status (approve/reject).
 */
export function useUpdateReviewStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }) => {
      const { error } = await supabase
        .from('testimonials')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return { id, status };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'review-stats'] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Toggle featured status.
 */
export function useToggleReviewFeatured() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, is_featured }) => {
      const { error } = await supabase
        .from('testimonials')
        .update({ is_featured, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return { id, is_featured };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
    },
  });
}

/**
 * Update review content (admin edit).
 */
export function useUpdateReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, updates }) => {
      const { error } = await supabase
        .from('testimonials')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
    },
  });
}

/**
 * Delete a review permanently.
 */
export function useDeleteReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from('testimonials')
        .delete()
        .eq('id', id);
      if (error) throw error;
      return { id };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'review-stats'] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

/**
 * Create a review (admin — auto-approved).
 */
export function useCreateReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ name, text, rating, product_id, is_featured }) => {
      const { data, error } = await supabase
        .from('testimonials')
        .insert({
          name,
          text,
          rating: rating || 5,
          product_id: product_id || null,
          is_featured: is_featured || false,
          status: 'approved',
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'reviews'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'review-stats'] });
      queryClient.invalidateQueries({ queryKey: ['reviews'] });
      queryClient.invalidateQueries({ queryKey: ['testimonials'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
  });
}

export const MAX_REVIEW_LENGTH = 500;
